import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImagePlus, X } from 'lucide-react';

import { greyP4, greyP5 } from '../../theme';
import { validateImageDimensions } from '../../utils/imageUtils';

const LOGO_REQUIRED_SIZE = 200;

interface AddManufacturerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddManufacturer: (name: string, logoBase64?: string) => void;
}

const AddManufacturerModal: React.FC<AddManufacturerModalProps> = ({ isOpen, onClose, onAddManufacturer }) => {
  const { t } = useTranslation();
  const [manufacturerName, setManufacturerName] = useState('');
  const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setManufacturerName('');
      setLogoBase64(undefined);
      setLogoPreview(null);
      setLogoError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manufacturerName.trim()) {
      onAddManufacturer(manufacturerName.trim(), logoBase64);
      setManufacturerName('');
      setLogoBase64(undefined);
      setLogoPreview(null);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError(null);
    const valid = await validateImageDimensions(file, LOGO_REQUIRED_SIZE, LOGO_REQUIRED_SIZE);
    if (!valid) {
      setLogoError(t('catalogue.logoSizeError', { size: LOGO_REQUIRED_SIZE }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      setLogoBase64(base64);
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoBase64(undefined);
    setLogoPreview(null);
    setLogoError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-manufacturer-modal-title"
        className="w-full max-w-md rounded-lg p-6"
        style={{ backgroundColor: greyP5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 id="add-manufacturer-modal-title" className="text-lg font-semibold text-white">
            {t('catalogue.addManufacturerTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-greyP3 hover:text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-greyP3 mb-2">{t('catalogue.manufacturerName')}</label>
            <input
              type="text"
              value={manufacturerName}
              onChange={(e) => setManufacturerName(e.target.value)}
              placeholder={t('catalogue.manufacturerNamePlaceholder')}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors"
              style={{ borderColor: greyP4 }}
              autoFocus
            />
          </div>

          {/* Logo Upload */}
          <div className="mb-6">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.manufacturerLogo')} ({t('common.optional')})
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoChange}
              className="hidden"
            />
            {logoPreview ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-black border" style={{ borderColor: greyP4 }}>
                <img src={logoPreview} alt="Logo preview" className="w-12 h-12 object-contain rounded" />
                <span className="text-sm text-white flex-1 truncate">{t('catalogue.logoUploaded')}</span>
                <button type="button" onClick={handleRemoveLogo} className="p-1 hover:bg-white/10 rounded text-red-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-black text-greyP3
                  border border-dashed hover:border-green01 hover:text-green01 transition-colors cursor-pointer"
                style={{ borderColor: greyP4 }}
              >
                <ImagePlus className="w-5 h-5" />
                <span className="text-sm">{t('catalogue.uploadLogo')}</span>
              </button>
            )}
            {logoError && <p className="mt-2 text-xs text-red-400">{logoError}</p>}
            <p className="mt-1 text-xs text-gray-500">{t('catalogue.logoSizeHint', { size: LOGO_REQUIRED_SIZE })}</p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-full text-white text-sm hover:bg-greyP4 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!manufacturerName.trim()}
              className="px-6 py-2 rounded-full bg-green01 text-black text-sm font-medium
                hover:bg-green03 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('catalogue.addManufacturer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddManufacturerModal;
